import React, {
    useEffect,
    useContext,
    useCallback,
    memo,
    useMemo,
    useReducer,
} from 'react';
import { withCookies } from 'react-cookie';

import Traductor from 'components/Traductor';
import Carousel from 'components/bronson/Carousel';
import Loading from 'components/bronson/Loading';
import Table from 'components/bronson/Table';
import MaintenanceTable from 'components/maintenance/MaintenanceTable';
import AccidentsTable from 'components/accidents/AccidentsTable';
import Accordion from 'components/bronson/ControlledAccordion';
import VehicleDescription from 'components/auctions/ModalDetailVehicle/VehicleDescription';
import VehicleDetails from 'components/auctions/ModalDetailVehicle/VehicleDetails';
import DownloadVehicleDetailsButtons from 'components/auctions/ModalDetailVehicle/DownloadVehicleDetails';

import { UserContext } from 'contexts/UserContext';
import {
    formatNumber,
    getLiteralVehicleDescription,
} from 'js/components/shared/vehicleFunctions';
import VehicleDetailService from 'services/VehicleDetailService';
import Globals from '../../../Globals';
import { formatDate, b64toBlob, getImageSource } from 'Functions';

import 'components/css/detailVehicle.css';

const initialDetailVehicleState = {
    loading: false,
    modelDescriptionModified: null,
    viewInLargeScreen: true,
    expertisesList: [],
    accidentsList: [],
    maintenancesList: [],
    expandedAccordions: {},
};

const detailVehicleReducer = (state, { type, payload }) => {
    switch (type) {
        case 'SET_STATUS':
            return { ...state, loading: payload };
        case 'SET_MODEL_DESCRIPTION':
            return { ...state, modelDescriptionModified: payload };
        case 'UPDATE_ACCORDION_STATUS':
            return {
                ...state,
                expandedAccordions: {
                    ...state.expandedAccordions,
                    [payload]: !state[payload],
                },
            };
        case 'SET_EXPERTISES':
            return {
                ...state,
                expertisesList: payload.data,
                expandedAccordions: {
                    ...state.expandedAccordions,
                    [payload.key]: true,
                },
            };
        case 'SET_ACCIDENTS':
            return {
                ...state,
                accidentsList: payload.data,
                expandedAccordions: {
                    ...state.expandedAccordions,
                    [payload.key]: true,
                },
            };
        case 'SET_MAINTENANCES':
            return {
                ...state,
                maintenancesList: payload.data,
                expandedAccordions: {
                    ...state.expandedAccordions,
                    [payload.key]: true,
                },
            };
        case 'UPDATE_VIEWPORT':
            return { ...state, viewInLargeScreen: payload };
        default:
            return state;
    }
};

const DetailVehicle = ({
    cookies,
    vehicle,
    onShowDetail,
    printCountDown,
    printFavouriteIcon,
    printWebSalesOptions,
    updateVehiclesList,
    tagMaxBidStatus,
    fetchServerTime,
    inactive,
}) => {
    const t = useMemo(() => new Traductor(), []);
    const { setIdExpertise } = useContext(UserContext);
    const [
        {
            loading,
            viewInLargeScreen,
            modelDescriptionModified,
            expertisesList,
            accidentsList,
            maintenancesList,
            expandedAccordions,
        },
        dispatch,
    ] = useReducer(detailVehicleReducer, initialDetailVehicleState);

    useEffect(() => {
        fetchServerTime();
    }, [fetchServerTime]);

    const getVehicleDetails = useCallback(async () => {
        let type = 6;
        if (vehicle?.HasDirectPurchaseModality) type = 7;
        if (vehicle.BuyerName) type = 8;
        if (vehicle.RepurchaseObligation >= 0) type = 4;

        if (vehicle?.HasRentingModality > 0) type = 12;

        const vehicleDetails = await new VehicleDetailService().getDetails(
            cookies.get(Globals.COOKIES.JWT)?.AccessToken,
            vehicle?.Id || vehicle?.IdVehicle,
            type
        );

        if (vehicleDetails?.body?.Result) {
            const MoreDetails = JSON.parse(vehicleDetails.body.Result);
            if (MoreDetails.Plate == null) {
                onShowDetail(false);
                alert('El detalle de este vehículo no está disponible.');
            } else {
                updateVehiclesList({ ...vehicle, MoreDetails });
            }
        } else {
            onShowDetail(false);
        }
    }, [cookies, onShowDetail, updateVehiclesList, vehicle]);

    useEffect(() => {
        if (!vehicle) return;
        dispatch({
            type: 'SET_MODEL_DESCRIPTION',
            payload: getLiteralVehicleDescription(vehicle),
        });
    }, [vehicle]);

    const getExpertisesList = useCallback(
        async key => {
            if (!expandedAccordions.hasOwnProperty(key)) {
                await new VehicleDetailService()
                    .getExpertisesList(
                        cookies.get(Globals.COOKIES.JWT)?.AccessToken,
                        vehicle.VIN || vehicle.Frame
                    )
                    .then(res => {
                        if (res?.body?.Result) {
                            dispatch({
                                type: 'SET_EXPERTISES',
                                payload: {
                                    data: JSON.parse(res.body.Result),
                                    key,
                                },
                            });
                        } else {
                            dispatch({ type: 'SET_STATUS', payload: false }); //error
                        }
                    });
            } else {
                dispatch({ type: 'UPDATE_ACCORDION_STATUS', payload: key });
            }
        },
        [cookies, expandedAccordions, vehicle.Frame, vehicle.VIN]
    );

    const getVehicleAccidents = useCallback(
        async key => {
            if (!expandedAccordions.hasOwnProperty(key)) {
                const vehicleAccidents = await new VehicleDetailService().getVehicleAccidents(
                    cookies.get(Globals.COOKIES.JWT)?.AccessToken,
                    vehicle.Plate,
                    vehicle?.Id || vehicle?.IdVehicle
                );
                dispatch({
                    type: 'SET_ACCIDENTS',
                    payload: {
                        data:
                            vehicleAccidents?.map(e => ({
                                ...e,
                                SinisterDate: formatDate(e.SinisterDate),
                                CompensationPrice: `${formatNumber(
                                    e.CompensationPrice
                                )}€`,
                            })) || [],
                        key,
                    },
                });
            } else {
                dispatch({ type: 'UPDATE_ACCORDION_STATUS', payload: key });
            }
        },
        [cookies, expandedAccordions, vehicle.Plate]
    );

    const getVehicleMaintenance = useCallback(
        async key => {
            if (!expandedAccordions.hasOwnProperty(key)) {
                const vehicleMaintenances = await new VehicleDetailService().getVehicleMaintenance(
                    cookies.get(Globals.COOKIES.JWT)?.AccessToken,
                    vehicle?.Id
                );
                dispatch({
                    type: 'SET_MAINTENANCES',
                    payload: {
                        data:
                            vehicleMaintenances?.map(e => ({
                                Factura: e.InvoiceNumber,
                                Date: formatDate(e.InvoiceDate),
                                Km: formatNumber(e.Km),
                                Dealer: e.Dealer,
                                DealerName: e.DealerName,
                                // Coverage: e.Coverage,
                                // Details: e.Details,
                            })) || [],
                        key,
                    },
                });
            } else {
                dispatch({ type: 'UPDATE_ACCORDION_STATUS', payload: key });
            }
        },
        [cookies, expandedAccordions, vehicle?.Id]
    );

    const onResizeWindow = useCallback(() => {
        dispatch({ type: 'UPDATE_VIEWPORT', payload: window.innerWidth > 960 });
    }, []);

    useEffect(() => {
        getVehicleDetails();
        onResizeWindow();
        window.addEventListener('resize', onResizeWindow);
        return () => window.removeEventListener('resize', onResizeWindow);
    }, []);

    useEffect(() => {
        setTimeout(() => {
            window.bronson.ComponentRegister.initAll();
        }, 300);
    }, [expandedAccordions]);

    const getExpertiseData = useCallback(
        async ExpertiseId => {
            const data = {
                ExpertiseId,
                Plate: vehicle.Plate,
                OnlyHtml: true,
            };
            dispatch({ type: 'SET_STATUS', payload: true });
            setIdExpertise(data);
            await new VehicleDetailService()
                .getExpertiseData(
                    cookies.get(Globals.COOKIES.JWT)?.AccessToken,
                    data
                )
                .then(expertiseData => {
                    if (
                        expertiseData?.body &&
                        JSON.parse(expertiseData.body).Result
                    ) {
                        if (!data.OnlyHtml) {
                            b64toBlob(
                                JSON.parse(expertiseData.body).Result,
                                'application/pdf'
                            );
                        } else {
                            const html = JSON.parse(expertiseData.body).Result;
                            const newWindow = window.open();
                            newWindow.document.write(html);
                        }
                    } else {
                        //alert('Error al obtener los datos del peritaje');
                    }
                    dispatch({ type: 'SET_STATUS', payload: false });
                });
        },
        [vehicle.Plate, setIdExpertise, cookies]
    );

    const printVehicleTitle = useCallback(() => {
        return (
            <div className="o-layout">
                <div className="o-layout__item u-1/1 c-detail-vehicle__titles">
                    <p className="u-h4 c-detail-vehicle__title">
                        {vehicle.MoreDetails.BrandName +
                            ' ' +
                            vehicle.MoreDetails.ModelName +
                            ' - ' +
                            vehicle.MoreDetails.Plate}
                    </p>
                    <p className="u-h6 c-detail-vehicle__subtitle">
                        {modelDescriptionModified
                            ? modelDescriptionModified
                            : vehicle.MoreDetails.ModelDescription}
                    </p>
                </div>
            </div>
        );
    }, [modelDescriptionModified, vehicle]);

    const printDefaultEquipaments = useCallback(() => {
        if (vehicle.MoreDetails.EquipmentsSeries?.length) {
            return (
                <ul className="c-icon-list">
                    {vehicle.MoreDetails.EquipmentsSeries.map(
                        (EquipmentsSeries, i) => (
                            <li key={`${i}`}>
                                <i
                                    className="c-icon-list__icon  c-icon  c-icon--[semantic-checkmark]"
                                    aria-hidden="true"
                                ></i>{' '}
                                <p>{EquipmentsSeries.DescriptionEquipment}</p>{' '}
                            </li>
                        )
                    )}
                </ul>
            );
        } else {
            return <p>{t.getTraduction('DataNoAvailable')}</p>;
        }
    }, [vehicle, t]);

    const printOptionalEquipaments = useCallback(() => {
        if (vehicle?.MoreDetails?.EquipmentsExtras?.length) {
            return (
                <ul className="c-icon-list">
                    {vehicle.MoreDetails.EquipmentsExtras.map(
                        (EquipmentsExtras, i) => (
                            <li key={`${i}`}>
                                <i
                                    className="c-icon-list__icon  c-icon  c-icon--[semantic-checkmark]"
                                    aria-hidden="true"
                                />{' '}
                                <p>{EquipmentsExtras.DescriptionEquipment}</p>{' '}
                            </li>
                        )
                    )}
                </ul>
            );
        } else {
            return <p>{t.getTraduction('DataNoAvailable')}</p>;
        }
    }, [vehicle, t]);

    const printVehicleDetails = useCallback(() => {
        const expertisesRows = expertisesList.map((item, i) => {
            return (
                <div
                    className="o-layout u-m-none u-mb"
                    key={`${item.ExpertiseId}${i}`}
                >
                    <div className="o-layout__item u-2/3 u-1/2@s u-pl-none u-text-bold">
                        {t.getTraduction('Expertsreport') +
                            ' ' +
                            formatDate(item.ExpertiseDate, false)}
                    </div>
                    <div className="o-layout__item u-text-right u-1/3 u-1/2@s u-pr-none">
                        <button
                            className="c-btn c-btn--secondary c-btn--icon"
                            onClick={() => getExpertiseData(item.ExpertiseId)}
                        >
                            <span className="c-btn__text">
                                {t.getTraduction('Download')}
                            </span>
                            <i className="c-btn__icon c-icon c-icon--[semantic-download]"></i>
                        </button>
                    </div>
                </div>
            );
        });

        return (
            <>
                <hr />
                <div className="c-detail-vehicle__list-especification u-mb-large">
                    <VehicleDetails vehicle={vehicle} t={t} />
                    <DownloadVehicleDetailsButtons
                        vehicle={vehicle}
                        t={t}
                        cookies={cookies}
                    />
                    {vehicle.MoreDetails.Observation ? (
                        <div className="o-layout__item u-1/1 u-pl-none u-mt-xlarge">
                            <div className="c-detail-vehicle__especification-observations u-text-center u-text-uppercase">
                                <p className="c-detail-vehicle__especification-title">
                                    {t.getTraduction('Observations')}
                                </p>
                                <p className="u-text-uppercase">
                                    {vehicle.MoreDetails.Observation}
                                </p>
                            </div>
                        </div>
                    ) : null}
                    {vehicle.MoreDetails.ObservationREBU ? (
                        <div className="o-layout__item u-1/1 u-pl-none u-mt-xlarge">
                            <div className="c-detail-vehicle__especification-observations u-text-center u-text-uppercase">
                                <p className="c-detail-vehicle__especification-title">
                                    {t.getTraduction('Observations')} REBU
                                </p>
                                <p className="u-text-uppercase">
                                    {vehicle.MoreDetails.ObservationREBU}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div
                    className="c-accordion  js-accordion" //js-is-fx removed to control accordion animation
                    data-accordion-multiple="true"
                    data-tr-state="Collapsed"
                >
                    <Accordion
                        label={t.getTraduction('EquipmentsSeriesTitle')}
                        content={printDefaultEquipaments()}
                    />
                    <Accordion
                        label={t.getTraduction('EquipmentsExtraTitle')}
                        content={printOptionalEquipaments()}
                    />
                    <Accordion
                        label={t.getTraduction('Expertsreports')}
                        content={expertisesRows}
                        expanded={
                            expandedAccordions[
                                t.getTraduction('Expertsreports')
                            ]
                        }
                        condition={expertisesList?.length}
                        onOpen={getExpertisesList}
                    />
                    {/* <Accordion
                        label={t.getTraduction('CarAccident')}
                        content={
                            <Table
                                data={{
                                    keys: [
                                        t.getTraduction('Date'),
                                        t.getTraduction('Description'),
                                        t.getTraduction('Import'),
                                        '',
                                    ],
                                    items: accidentsList,
                                }}
                                options={{
                                    classes: { body: { 3: 'u-text-right' } },
                                }}
                            />
                        }
                        expanded={
                            expandedAccordions[t.getTraduction('CarAccident')]
                        }
                        condition={accidentsList?.length}
                        onOpen={getVehicleAccidents}
                    /> */}
                    <Accordion
                        label={t.getTraduction('CarMaintenance')}
                        content={
                            <MaintenanceTable
                                cookies={cookies}
                                data={{
                                    keys: [
                                        'Factura',
                                        t.getTraduction('Date'),
                                        t.getTraduction('Kms'),
                                        t.getTraduction('Concesionario'),
                                        '',
                                    ],
                                    items: maintenancesList,
                                }}
                                vehicleId={vehicle?.Id}
                            />
                        }
                        expanded={
                            expandedAccordions[
                                t.getTraduction('CarMaintenance')
                            ]
                        }
                        condition={maintenancesList?.length}
                        onOpen={getVehicleMaintenance}
                    />
                    <Accordion
                        label={t.getTraduction('CarAccident')}
                        content={
                            <AccidentsTable
                                cookies={cookies}
                                data={{
                                    keys: [
                                        t.getTraduction('Date'),
                                        t.getTraduction('Description'),
                                        t.getTraduction('Import'),
                                        '',
                                    ],
                                    items: accidentsList,
                                }}
                                vehicleId={vehicle?.Id}
                            />
                        }
                        expanded={
                            expandedAccordions[t.getTraduction('CarAccident')]
                        }
                        condition={accidentsList?.length}
                        onOpen={getVehicleAccidents}
                    />
                </div>
            </>
        );
    }, [
        expertisesList,
        t,
        vehicle,
        printDefaultEquipaments,
        printOptionalEquipaments,
        expandedAccordions,
        getExpertisesList,
        accidentsList,
        getVehicleAccidents,
        maintenancesList,
        getVehicleMaintenance,
        getExpertiseData,
    ]);

    const printCarousel = useCallback(() => {
        const optionsCarousel = {
            id: 'c-detail-vehicle__carousel-' + vehicle.Id,
            next: true,
            images: vehicle?.MoreDetails?.ImageSource?.length
                ? vehicle.MoreDetails.ImageSource
                : [getImageSource()],
            flickity: {
                imagesLoaded: false,
            },
        };

        return (
            <Carousel options={optionsCarousel} onClickFunction={() => null} />
        );
    }, [vehicle.Id, vehicle?.MoreDetails?.ImageSource]);

    if (vehicle?.MoreDetails) {
        return (
            <div
                className="c-modal c-modal--left c-modal--detail-vehicle"
                aria-hidden={'false'}
                id="detail-vehicle-modal"
            >
                <div
                    className="c-modal__overlay  js-modal-close"
                    tabIndex="-1"
                />
                <div
                    className="c-modal__wrapper"
                    role="dialog"
                    aria-labelledby="Vehicle detail modal"
                >
                    <header className="c-modal__header">
                        <button
                            onClick={() => onShowDetail(false)}
                            className="c-modal__close-btn  js-modal-close"
                            type="button"
                            aria-label="Cerrar modal."
                        />
                    </header>
                    <div
                        className={`c-modal__content ${inactive}`}
                        role="document"
                    >
                        <div className="o-layout c-detail-vehicle js-detail-vehicle">
                            <div className="o-layout__item u-3/5 u-1/1@m">
                                <div
                                    className={`c-detail-vehicle__carousel-container ${
                                        vehicle?.MoreDetails?.ImageSource
                                            ?.length > 10
                                            ? 'withoutdots'
                                            : ''
                                    }`}
                                >
                                    {printCarousel()}
                                    {printFavouriteIcon()}
                                    {printCountDown()}
                                    {tagMaxBidStatus()}
                                    {vehicle.IsBlocked && (
                                        <p className="c-vehicle-card__watermark">
                                            {t.getTraduction('Reserved')}
                                        </p>
                                    )}
                                    {vehicle.Status > 2 && (
                                        <p className="c-vehicle-card__watermark">
                                            {t.getTraduction('Sold')}
                                        </p>
                                    )}
                                </div>
                                {!viewInLargeScreen
                                    ? printVehicleTitle()
                                    : null}
                                <VehicleDescription vehicle={vehicle} t={t} />
                                {!viewInLargeScreen
                                    ? printWebSalesOptions({ cookies, vehicle })
                                    : null}
                                {printVehicleDetails()}
                            </div>
                            {viewInLargeScreen ? (
                                <div className="o-layout__item u-2/5 u-1/1@m c-detail-vehicle__sticky-parent">
                                    <div className="c-detail-vehicle__sticky-body">
                                        {printVehicleTitle()}
                                        {printWebSalesOptions({
                                            cookies,
                                            vehicle,
                                        })}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
                <Loading ariaBusy={loading} />;
            </div>
        );
    } else {
        return <Loading ariaBusy={true} />;
    }
};

export default memo(withCookies(DetailVehicle));
